import {AfterViewInit, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {PeriodsService} from '../../../../../shared/services/periods.service';
import {forkJoin, merge, Observable, of, Subscription, withLatestFrom} from 'rxjs';
import {ReportUtils} from '../../../../../shared/libraries/report-utils';
import {ComponentType} from '@angular/cdk/overlay';
import {first} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {Store} from "@ngrx/store";
import {AppState} from "../../../../../shared/store/store";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {AdReportParams, AdRPeriod} from '../../../../../shared/interfaces/ad-reports';
import {DashboardWidget, DashboardWidgetConfig} from '../../../../../shared/store/report/report-dashboard';
import {reportDashboardAddWidget, reportDashboardUpdateWidget} from '../../../../../shared/store/report/report.actions';
import {selectDimensionComponentObjects, selectMetricComponentObjects} from '../../../../../shared/store/selectors';
import {DimensionComponentObject, DimensionComponentObjects} from '../../../../../shared/classes/dimension-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../../../../../shared/classes/metric-component-object';
import {DialogData} from '../../../../../shared/interfaces/dialog';

@Component({
  selector: 'app-configure-base',
  template: '',
  styleUrls: ['./configure-base.component.scss'],
})
export class ConfigureBaseComponent implements OnInit, OnDestroy, AfterViewInit {
  public      form:                 FormGroup =                 new FormGroup({});
  public      dimensions:           Array<DimensionComponentObject> = [];
  public      metrics:              Array<MetricComponentObject> = [];
  public      periods:              Array<Partial<AdRPeriod>> = this.periodsS.getPeriods();
  protected   widgetComponent:      ComponentType<any>;
  public      formChanges:          any =                       {};
  private     dataSubs:             Subscription;
  private     formAutocompleteSubs: Subscription;
  private     _autocompleteSubscription: Subscription;

  protected _dimensions$: Observable<Array<DimensionComponentObject>> = this._store.select(selectDimensionComponentObjects);
  protected _metrics$: Observable<Array<MetricComponentObject>> = this._store.select(selectMetricComponentObjects);

  public readonly dashboardCtrl: FormControl = new FormControl(null, [Validators.required]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogData & any,
    public readonly dialogRef: MatDialogRef<any>,
    public readonly periodsS: PeriodsService,
    protected readonly translateS: TranslateService,
    protected readonly _store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this._autocompleteSubscription = merge(
      this.form.get('dimension')?.valueChanges || of(),
      this.form.get('metric')?.valueChanges || of(),
      this.form.get('dimensions')?.valueChanges || of(),
      this.form.get('metrics')?.valueChanges || of(),
      this.form.get('totals')?.valueChanges || of(),
      this.form.get('curves')?.valueChanges || of()
    )
      .pipe(
        withLatestFrom(this._dimensions$, this._metrics$),
      )
      .subscribe(([_, dimensions, metrics]): void => {
        if (this.form.contains('metrics') && this.form.contains('dimensions')) {
          const splittedTitle: Array<string> = this.form.get('title').value?.split(this.translateS.instant('dashboard_report.by')) || [];
          const dimensionNames: Array<string> = splittedTitle[1]?.split(', ') || [];
          const metricNames: Array<string> = splittedTitle[0]?.split(', ') || [];
          const canModify: Array<boolean> = [
            ...dimensionNames.map((name: string): boolean => dimensions.find((dimensionComponentObject: DimensionComponentObject): boolean => name.includes(dimensionComponentObject.payload.attributes.name)) !== undefined),
            ...metricNames.map((name: string): boolean => metrics.find((metricComponentObject: MetricComponentObject): boolean => name.includes(metricComponentObject.payload.attributes.name)) !== undefined)
          ];

          if (this.form.get('metrics').value && this.form.get('dimensions').value && (!this.form.get('title').value || !canModify.includes(false))) {
            this.form.get('title').setValue(`${this.form.get('metrics').value.map(metric => metric.payload.attributes.name).join(', ')} ${ this.translateS.instant('dashboard_report.by') } ${this.form.get('dimensions').value.map(dimension => dimension.payload.attributes.name).join(', ')}`);
            this.formChanges['title'] = false;
          } else {
            this.formChanges['title'] = true;
          }
        } else if (this.form.contains('metric') && this.form.contains('dimension')) {
          if (this.form.get('dimension').value && this.form.get('metric').value) {
            if (!this.form.get('title').value || metrics.find(metricComponentObject =>  this.form.get('title').value?.includes(metricComponentObject.payload.attributes.name)) && dimensions.find(dimensionComponentObject =>  this.form.get('title').value?.includes(dimensionComponentObject.payload.attributes.name))) {
              this.form.get('title').setValue(`${ this.form.get('dimension').value.payload.attributes.name } ${ this.translateS.instant('dashboard_report.by') } ${ this.form.get('metric').value.payload.attributes.name }`, { emitEvent: false });
              this.formChanges['title'] = false;
            } else {
              this.formChanges['title'] = true;
            }
          }
        } else if (this.form.contains('metric') && this.form.contains('name')) {
          if (!this.form.get('name').value || metrics.find(metricComponentObject =>  this.form.get('name').value.includes(metricComponentObject.payload.attributes.name))) {
            this.form.get('name').setValue(this.form.get('metric').value.payload.attributes.name, { emitEvent: false });
            this.formChanges['name'] = false;
          } else {
            this.formChanges['name'] = true;
          }
        } else if (
          (this.form.contains('totals') && this.form.get('totals').value && this.data.totals) ||
          (this.form.contains('curves') && this.form.get('curves').value && this.data.curves)
        ) {
          const names: Array<string> = this.form.get('title').value?.split(', ') || [];
          const canModify: Array<boolean> = names.map((name: string): boolean => metrics.find((metricComponentObject: MetricComponentObject): boolean => name.includes(metricComponentObject.payload.attributes.name)) !== undefined);

          if (!this.form.get('title').value || !canModify.includes(false)) {
            this.form.get('title').setValue((this.form.get('totals') || this.form.get('curves')).value.map((config: any): string => config.metric.payload.attributes.name).join(', '), { emitEvent: false });
            this.formChanges['title'] = false;
          } else {
            this.formChanges['title'] = true;
          }
        }
      });
  }

  ngAfterViewInit(): void {
    this.dataSubs = forkJoin([
      (this._dimensions$)
        .pipe(first()),
      this._metrics$
        .pipe(first())
    ])
      .subscribe(([dimensions, metrics]): void => {
        this.dimensions = dimensions;
        this.metrics = metrics;

        this.initForm();
      });
  }

  ngOnDestroy(): void {
    ReportUtils.unsubscribe(this.dataSubs);
    ReportUtils.unsubscribe(this.formAutocompleteSubs);
    this._autocompleteSubscription.unsubscribe();
  }

  private initFormControls(config: any): void {
    for (const key in config) {
      const keyWithoutS: string = key.substr(0, key.length - 1);
      const newKey: string = this.form.contains(key) && key || this.form.contains(keyWithoutS) && keyWithoutS || null;

      if (newKey) {
        switch (newKey) {
          case 'dashboard':
            break;
          case 'dimensions':
          case 'metrics':
            this.form.get(newKey)
              .setValue(
                config[key].reduce(
                  (output: MetricComponentObjects | DimensionComponentObjects, slug: string) => {
                    const item: MetricComponentObject & DimensionComponentObject = (this[key] as Array<DimensionComponentObject & MetricComponentObject>).find((item: DimensionComponentObject | MetricComponentObject) => item.payload.attributes.slug === slug)

                    if (item) {
                      output.push(item);
                    }

                    return output;
                  },
                  []
                )
              );
            break;
          case 'dimension':
          case 'metric':
            this.form.get(newKey).setValue((this[key] as Array<DimensionComponentObject | MetricComponentObject>).find(item => item.payload.attributes.slug === config[key][0]));
            break;
          case 'period':
            this.form.get(key).setValue(Object.assign(this.periods.find(period => period.type === config[key].type) || this.periods.find(period => period.type === 'last_30_days'), {
              ...config[key],
              compareWith: config['compare']?.value || null
            }));
            break;
          case 'type':
            this.form.get(key).setValue(config.area && 'area' || config[key]);
            break;
          default:
            this.form.get(key).setValue(config[key]);
            break;
        }
      }
    }
  }

  protected initForm(): void {
    if (this.data && this.data.config || this.data.widget?.config) {
      this.initFormControls(this.data.config || this.data.widget.config);
    }
  }

  protected getConfig(): Partial<DashboardWidgetConfig> {
    return {};
  }

  protected getParams(): Partial<AdReportParams> {
    return {};
  }

  protected getDashboard(): DashboardWidget[] {
    return this.dashboardCtrl.value;
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    if (this.data.widget) {
      this._store.dispatch(reportDashboardUpdateWidget({
        widget: this.data.widget,
        params: this.getParams(),
        config: this.getConfig(),
        dashboard: this.getDashboard()
      }));
    } else {
      this._store.dispatch(reportDashboardAddWidget({
        widget: this.widgetComponent,
        params: this.getParams(),
        config: this.getConfig(),
        dashboard: this.getDashboard()
      }));
    }
    this.dialogRef.close();
  }











}
